/**
 * Browser detection utilities for handling platform-specific CSS issues
 */
import { useMemo } from 'react';

export const detectBrowser = () => {
  const userAgent = navigator.userAgent.toLowerCase();
  
  return {
    isWechatDesktop: userAgent.includes('micromessenger') && !userAgent.includes('mobile'),
    isWechatMobile: userAgent.includes('micromessenger') && userAgent.includes('mobile'),
    isWechat: userAgent.includes('micromessenger'),
    isIOS: /iphone|ipad|ipod/.test(userAgent),
    isMobile: /android|webos|iphone|ipad|ipod|blackberry|iemobile|opera mini/.test(userAgent),
    isDesktop: !/android|webos|iphone|ipad|ipod|blackberry|iemobile|opera mini/.test(userAgent)
  };
};

/**
 * Get safe CSS values based on browser capabilities
 */
export const getSafeCSS = () => {
  const browser = detectBrowser();
  
  return {
    // Height values - use vh for WeChat desktop, dvh for modern browsers
    fullHeight: browser.isWechatDesktop ? '100vh' : '100dvh',
    minFullHeight: browser.isWechatDesktop ? 'min-h-screen' : 'min-h-[100dvh]',
    
    // Safe area insets - only disable for WeChat desktop
    topSafeArea: browser.isWechatDesktop ? '0px' : 'env(safe-area-inset-top)',
    bottomSafeArea: browser.isWechatDesktop ? '0px' : 'env(safe-area-inset-bottom)',
    
    // Calc functions - preserve safe area for non-WeChat desktop
    getCalcHeight: (subtract: string) => {
      if (browser.isWechatDesktop) {
        return `calc(100vh - ${subtract})`;
      }
      // For iOS and other modern browsers, include safe area
      return `calc(100dvh - ${subtract} - env(safe-area-inset-top))`;
    },
    
    // CSS classes
    getHeightClass: (fallback: string) => 
      browser.isWechatDesktop ? fallback : 'min-h-[100dvh]'
  };
};

/**
 * Hook for reactive browser detection with memoization
 */

export const useBrowserDetection = () => {
  const { browser, safeCSS } = useMemo(() => {
    const detectedBrowser = detectBrowser();
    const cssValues = getSafeCSS();
    return { 
      browser: detectedBrowser, 
      safeCSS: cssValues 
    };
  }, []); // Empty dependency array since browser doesn't change during session
  
  return { browser, safeCSS };
};